/**
 * 이메일 형식이 유효한지 검사하는 함수
 * @param email 검사할 이메일 문자열
 * @returns 유효한 이메일 형식이면 true, 아니면 false
 */
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email);
};

/**
 * 웹사이트 URL 형식이 유효한지 검사하는 함수
 * @param url 검사할 URL 문자열
 * @returns 유효한 URL 형식이면 true, 아니면 false
 */
export const isValidURL = (url: string): boolean => {
  if (!url || url.trim() === '') return true; // 빈 값은 허용
  
  const urlRegex = /^https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)$/;
  return urlRegex.test(url.trim());
};

/**
 * 사업자 등록번호 형식이 유효한지 검사하는 함수
 * @param businessNumber 검사할 사업자 등록번호 문자열
 * @returns 유효한 사업자 등록번호 형식이면 true, 아니면 false
 */
export const isValidBusinessNumber = (businessNumber: string): boolean => {
  const businessNumberRegex = /^\d{3}-\d{2}-\d{5}$/;
  return businessNumberRegex.test(businessNumber);
};

/**
 * 사업자 등록번호 형식이 유효한지 검사하는 함수
 * @param businessNumber 검사할 사업자 등록번호 문자열
 * @returns 유효한 사업자 등록번호 형식이면 true, 아니면 false
 */
export const isValidResidentNumber = (residentNumber: string): boolean => {
  const residentNumberRegex = /^\d{6}-\d{7}$/;
  return residentNumberRegex.test(residentNumber);
};

/**
 * 마스킹된 주민등록번호(예: 900101-1******) 형식이 유효한지 검사하는 함수
 * @param residentNumber 검사할 주민등록번호 문자열
 * @returns 유효한 마스킹 주민등록번호 형식이면 true, 아니면 false
 */
export const isValidResidentNumberWithAutoMask = (residentNumber: string): boolean => {
  // 6자리-1자리(성별) + 6자리 * 또는 숫자 허용 (예: 900101-1****** 또는 900101-1234567)
  const maskedResidentNumberRegex = /^\d{6}-[1-4][0-9*]{6}$/;
  return maskedResidentNumberRegex.test(residentNumber);
};

/**
 * 전화번호 형식이 유효한지 검사하는 함수
 * @param phoneNumber 검사할 전화번호 문자열
 * @returns 유효한 전화번호 형식이면 true, 아니면 false
 */
export const isValidPhone = (phoneNumber: string): boolean => {
  const phoneRegex = /^\d{3}-\d{4}-\d{4}$/;
  return phoneRegex.test(phoneNumber);
};



/**
 * 숫자를 핸드폰 번호 형식으로 변환하는 함수
 * @param phoneNumber 변환할 전화번호 문자열
 * @returns 포맷팅된 전화번호 문자열 (예: 010-1234-5678)
 */
export const formatPhoneNumber = (phoneNumber: string): string => {
  // 숫자가 아닌 문자 제거
  const cleaned = phoneNumber.replace(/\D/g, '');
  
  // 11자리가 아닌 경우 원본 반환
  if (cleaned.length !== 11) {
    return cleaned;
  }

  // 010-XXXX-XXXX 형식으로 변환
  return cleaned.replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3');
};

/**
 * 사업자 등록번호를 형식화하는 함수
 * @param businessNumber 변환할 사업자 등록번호 문자열
 * @returns 포맷팅된 사업자 등록번호 문자열 (예: XXX-XX-XXXXX)
 */
export const formatBusinessNumber = (businessNumber: string): string => {
  // 숫자가 아닌 문자 제거
  const cleaned = businessNumber.replace(/\D/g, '');
  
  // 10자리가 아닌 경우 원본 반환
  if (cleaned.length !== 10) {
    return cleaned;
  }

  // XXX-XX-XXXXX 형식으로 변환
  return cleaned.replace(/(\d{3})(\d{2})(\d{5})/, '$1-$2-$3');
};

/**
 * 주민등록번호를 형식화하는 함수
 * @param residentNumber 변환할 주민등록번호 문자열
 * @returns 포맷팅된 주민등록번호 문자열 (예: XXXXXX-XXXXXXX)
 */
export const formatResidentNumber = (residentNumber: string): string => {
  // 숫자가 아닌 문자 제거
  const cleaned = residentNumber.replace(/\D/g, '');
  
  // 13자리가 아닌 경우 원본 반환
  if (cleaned.length !== 13) {
    return cleaned;
  }

  // XXXXXX-XXXXXXX 형식으로 변환
  return cleaned.replace(/(\d{6})(\d{7})/, '$1-$2');
};

/**
 * 출원번호를 형식화하는 함수
 * @param applicationNumber 변환할 출원번호 문자열
 * @returns 포맷팅된 출원번호 문자열 (예: XX-XXXX-XXXXXXXX)
 */
export const formatApplicationNumber = (applicationNumber: string): string => {
  // 숫자가 아닌 문자 제거
  const cleaned = applicationNumber.replace(/\D/g, '');

  // 10자리가 아닌 경우 원본 반환
  if (cleaned.length !== 13) {
    return cleaned;
  }

  // XX-XXXX-XXXXXXXX 형식으로 변환
  return cleaned.replace(/(\d{2})(\d{4})(\d{7})/, '$1-$2-$3');
};

/**
 * 특수문자를 제거하는 함수
 * @param text 특수문자를 제거할 문자열
 * @returns 특수문자가 제거된 문자열
 */
export const removeSpecialCharacters = (text: string): string => {
  // 숫자와 영문자를 제외한 모든 문자 제거
  return text.replace(/[^a-zA-Z0-9가-힣]/g, '');
};


export const removeComma = (text: string): string => {
  // 쉼표 제거
  return text.replace(/,/g, '');
};




/**
 * 주민등록번호를 마스킹 처리하는 함수
 * @param residentNumber 마스킹할 주민등록번호 문자열
 * @returns 마스킹된 주민등록번호 문자열 (예: XXXXXX-X******)
 */
export const maskResidentNumber = (residentNumber: string): string => {
  // 숫자가 아닌 문자 제거
  const cleaned = residentNumber.replace(/\D/g, '');
  
  // 13자리가 아닌 경우 원본 반환
  if (cleaned.length !== 13) {
    return cleaned;
  }

  // XXXXXX-X****** 형식으로 변환
  const first = cleaned.slice(0, 6);
  const second = cleaned.slice(6, 7);
  const masked = '*'.repeat(6);

  return `${first}-${second}${masked}`;
};

/**
 * 주민등록번호 입력 시 자동 마스킹 처리하는 함수
 * 7자리 입력 시 자동으로 마스킹된 형태로 변환
 * @param input 입력된 주민등록번호 문자열
 * @returns 포맷팅된 주민등록번호 문자열 (예: 823674-1******)
 */
export const formatResidentNumberWithAutoMask = (input: string): string => {
  // 숫자가 아닌 문자 제거 (대시와 별표 제외)
  const cleaned = input.replace(/[^\d-*]/g, '');
  
  // 이미 대시가 있는 경우의 처리
  if (cleaned.includes('-')) {
    const parts = cleaned.split('-');
    const beforeDash = parts[0].replace(/\D/g, '');
    
    // 대시 앞부분이 6자리보다 길면 6자리로 자름
    if (beforeDash.length > 6) {
      return beforeDash.slice(0, 6);
    }
    
    // 이미 마스킹된 형태라면 그대로 반환
    if (parts[1] && parts[1].includes('*')) {
      return cleaned;
    }
    
    // 완전한 주민등록번호인 경우 (13자리) 마스킹 처리
    const afterDash = parts[1] ? parts[1].replace(/\D/g, '') : '';
    const totalNumbers = beforeDash + afterDash;
    if (totalNumbers.length >= 7) {
      const first6 = beforeDash.length >= 6 ? beforeDash.slice(0, 6) : beforeDash;
      const seventh = totalNumbers.slice(6, 7);
      return `${first6}-${seventh}******`;
    }
    
    // 대시 앞부분만 반환 (6자리 이하)
    return beforeDash;
  }
  
  // 숫자만 추출
  const numbers = cleaned.replace(/\D/g, '');
  
  // 7자리 이후 입력 제한
  const limitedNumbers = numbers.slice(0, 7);
  
  // 6자리 이하인 경우 그대로 반환
  if (limitedNumbers.length <= 6) {
    return limitedNumbers;
  }
  
  // 7자리인 경우 마스킹 처리
  const first6 = limitedNumbers.slice(0, 6);
  const seventh = limitedNumbers.slice(6, 7);
  
  return `${first6}-${seventh}******`;
};
